import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, array, object, string } from 'yup';

export type SetupProviderFormState = {
  name: string | null;
  apiUrl: string | null;
  syncRoundEnd: string | boolean | null;
  status: string;
  retryScheduledSendCredit: string | boolean | null;
  retryConfigId: string | null | undefined;
  refundOnCreditFail: string | boolean | null;
  filterExternalProviderBets: string | boolean | null;
  isInternal: string | boolean | null;
  configId: string | undefined;
  creditOnDisconnect: string | boolean | null;
  games: number[];
  integrationType: string | null;
};

export const setupProviderFormStateDefault: SetupProviderFormState = {
  name: '',
  apiUrl: '',
  syncRoundEnd: '',
  status: '',
  retryScheduledSendCredit: '',
  retryConfigId: '',
  refundOnCreditFail: '',
  filterExternalProviderBets: '',
  isInternal: '',
  configId: undefined,
  creditOnDisconnect: '',
  games: [],
  integrationType: '',
};

export const setupProviderSchemaDefault: ObjectSchema<Partial<SetupProviderFormState>> =
  object<SetupProviderFormState>().shape({
    name: string().required('name is required field'),
    apiUrl: string().required('api url is required field'),
    syncRoundEnd: string().required('sync round end is required field'),
    status: string().required('status is required field'),
    retryScheduledSendCredit: string().required('retry scheduled send credit is required field'),
    retryConfigId: string().optional(),
    refundOnCreditFail: string().required('refund on credit fail is required field'),
    filterExternalProviderBets: string().required(
      'filter external provider bets is required field'
    ),
    isInternal: string().required('is internal is required field'),
    configId: string().optional(),
    creditOnDisconnect: string().required('credit on disconnect is required field'),
    games: array()
      .min(1, 'games array must have at least one game')
      .required('games is required field'),
    integrationType: string().required('integration type is required field'),
  });

type SetupProviderFormContextType = {
  schema: ObjectSchema<Partial<SetupProviderFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<SetupProviderFormState>>>;
  methods?: UseFormReturn<SetupProviderFormState>;
};

const createProviderFormContextDefault = {
  schema: setupProviderSchemaDefault,
  setSchema: noop,
} satisfies SetupProviderFormContextType;

export const SetupProviderFormContext = createContext<SetupProviderFormContextType>(
  createProviderFormContextDefault
);

export const useSetupProviderForm = () => {
  const { methods } = useContext(SetupProviderFormContext);
  if (!methods) {
    throw new Error('SetupProviderFormContext is empty');
  }

  return methods;
};

export const useManageSetupProviderSchema = () => {
  const { setSchema } = useContext(SetupProviderFormContext);

  return { setSchema };
};
